import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Config } from "../config";
import { useLoading } from "../hooks/use-loading";
import { User } from "../api/models/User";
import { useLogin } from "../api/useLoginApi";
import { useFetchTokenByAuthCode } from "../hooks/auth/use-fetch-token-by-auth-code";
import { useRefreshAuthToken } from "../hooks/auth/use-refresh-auth-token";

interface IAuthContextProps {
  token: string | null;
  user: User | null;
  loadings: {
    fetchUser: boolean;
  };
  logoutAction: () => void;
  refreshToken: () => any;
}

// @ts-ignore
const AuthContext = createContext<IAuthContextProps>();

export const AuthProvider: React.FC<any> = ({ children }) => {
  const api = useLogin();
  const { loadings, startLoading, stopLoading } = useLoading({
    fetchUser: false,
  });

  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(
    window.localStorage.getItem("CURRENT_USER_TOKEN"),
  );

  const { fetchTokenAfterLogin } = useFetchTokenByAuthCode();
  const { refreshToken } = useRefreshAuthToken();

  const authorize = useCallback(async () => {
    let fetchedToken: string | null | undefined;
    let redirectUrl = null;
    const needFetchTokenFromCode = token == null;

    try {
      if (needFetchTokenFromCode) {
        [fetchedToken, redirectUrl] = await fetchTokenAfterLogin();
      } else {
        fetchedToken = await refreshToken();
      }
    } catch (e) {
      fetchedToken = null;
    }

    handleUpdateToken(fetchedToken ?? null, redirectUrl);
  }, [token]);

  useEffect(() => {
    authorize().then(() => fetchUser());
  }, []);

  // /**
  //  * После получения токена ставим задачу обновить его до того времени пока она истечёт
  //  */
  // useEffect(() => {
  //   let timer: NodeJS.Timer | null = null;
  //   if (token) {
  //     timer = setTimeout(async () => {
  //       const token = await refreshToken();
  //       handleUpdateToken(token);
  //     }, 30_000);
  //   }
  //
  //   return () => {
  //     if (timer) {
  //       clearTimeout(timer);
  //     }
  //   };
  // }, [token]);

  const handleUpdateToken = useCallback(
    (token: string | null, redirectUrl?: string | null) => {
      setToken(token);
      if (token != null) {
        window.localStorage.setItem("CURRENT_USER_TOKEN", token);
      } else {
        window.localStorage.removeItem("CURRENT_USER_TOKEN");
        const redirect_uri = encodeURIComponent(
          window.location.protocol +
            "//" +
            window.location.host +
            "/auth-callback",
        );

        const state = encodeURIComponent(window.location.href);
        window.location.href =
          redirectUrl ??
          Config.KEYCLOAK_URL +
            "&redirect_uri=" +
            redirect_uri +
            `&state=${state}`;
      }
    },
    [],
  );

  const fetchUser = useCallback(async () => {
    try {
      startLoading("fetchUser");
      const result = await api.getCurrentUser();
      setUser(result);
    } catch (e) {
      setUser(null);
      handleUpdateToken(null);
    } finally {
      stopLoading("fetchUser");
    }
  }, []);

  const logoutAction = useCallback(() => {
    setUser(null);
    const redirect_uri = encodeURIComponent(
      window.location.protocol + "//" + window.location.host + "/auth-callback",
    );

    handleUpdateToken(
      null,
      `${Config.KEYCLOAK_LOGOUT_URL}redirect_uri=${redirect_uri}`,
    );
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        logoutAction,
        loadings,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}
